import React from 'react';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import styled from '../styled';
import { Tooltip } from 'antd';
import { floor } from '../../../../util/lodashlike';

const { mlr4 } = styled;

// FIN-13184 — these are TYPOLOGY-level model values on a 0-1 scale. The alert chip above this
// panel shows unified_alerts.ALERT_SCORE, which is the same quantity x100 (measured on
// keycloak-qa: exactly 100x on 11,259 of 11,259 single-typology alerts). Nothing was wrong with
// either number - but neither surface said which scale it was on, so 3.3666 next to 0.033666
// reads as a 100x error. The tooltip now names the scale instead of showing a bare 17-digit
// float. Display-only: no stored value changes, and the chip keeps the 0-100 score that the
// queue, alert lists, exports and CM all read.
// Scale ONLY, deliberately. An earlier version added "the alert score above is this x100",
// which is wrong twice on this component (review, #11710): it is shared with TM
// SmartAssessment and DetailedAlertInvestigation, where there is no chip "above" at all,
// and on a multi-typology alert only the HIGHEST typology satisfies that relation -
// ALERT_SCORE = MAX(typology) x 100, not each row x 100.
const SCALE_NOTE = 'model value on a 0-1 scale';

// FIN-13184 (secondary ask) — the tooltip printed the raw 17-significant-digit float
// (0.03366658080191822) while the cell showed it floored to 2dp. Both now TRUNCATE, the cell to
// 2dp and the tooltip to 6dp (the ticket's own example: 0.03366658... -> 0.033666, which is
// truncation, not rounding), so the tooltip is always the cell's value with more digits and can
// never read as a different number (rounding would turn 0.0299999 into a 0.03 tooltip over a
// 0.02 cell).
//
// Exponent-shifted, as lodash's own floor is, rather than `Math.floor(v * 10^dp) / 10^dp`:
// 0.29 * 100 is 28.999999999999996 in IEEE-754, which floors 0.29 to 0.28. Non-numeric input
// keeps the legacy lodashlike floor behaviour (null -> 0).
const TOOLTIP_DP = 6;
const CELL_DP = 2;

const shift = (n, dp) => {
  const [mantissa, exp = '0'] = `${n}`.split('e');
  return Number(`${mantissa}e${Number(exp) + dp}`);
};

export const truncateTo = (value, dp) => {
  const n = Number(value);
  if (value == null || value === '' || !Number.isFinite(n)) {
    return floor(value, dp);
  }
  return shift(Math.floor(shift(n, dp)), -dp);
};

export const withScale = (raw) => {
  if (raw == null || raw === '') return raw;
  const n = Number(raw);
  const shown = Number.isFinite(n) ? truncateTo(n, TOOLTIP_DP) : raw;
  return `${shown} (${SCALE_NOTE})`;
};

const PanelExtraTemplate = ({ threshold = null, value = null }) => {
  return (
    <div style={{ display: 'flex' }} className="widgitsView">
      <b>Threshold :</b>
      <Tooltip title={withScale(threshold)}>
        <span style={mlr4}>{truncateTo(threshold, CELL_DP)}</span>
      </Tooltip>
      <b>Values :</b>
      <Tooltip title={withScale(value)}>
        <span style={mlr4}>{truncateTo(value, CELL_DP)}</span>
      </Tooltip>
      {threshold < value && <ArrowUpOutlined style={{ color: 'red' }} />}
      {threshold > value && <ArrowDownOutlined style={{ color: 'navy' }} />}
    </div>
  );
};
export default PanelExtraTemplate;
